import { useEffect, useMemo, useState } from 'react';
import * as XLSX from 'xlsx';
import {
  departmentLabel,
  errorMessage,
  financeGet,
  monthRange,
  num,
  pad2,
  percent,
  todayValue,
  type FinanceCtx,
  type FinanceSettings,
} from '../financeApi';
import PrintPortal from '../common/PrintPortal';
import BulletinSummary from './BulletinSummary';

type Settlement = {
  from: string;
  to: string;
  church: FinanceSettings;
  carriedOver: number;
  income: number;
  expense: number;
  closing: number;
  byCategory: { type: '수입' | '지출'; category: string; total: number; count: number }[];
  byDepartment: { department: string; total: number }[];
  byMonth: { month: string; type: '수입' | '지출'; total: number }[];
  budgets: { type: '수입' | '지출'; category: string; total: number }[];
  budgetYear: number | null;
};

type Kind = 'month' | 'quarter' | 'year' | 'range';

function lastDay(year: number, month: number) {
  return new Date(year, month, 0).getDate();
}

function periodOf(kind: Kind, year: number, month: number, quarter: number, range: { from: string; to: string }) {
  if (kind === 'month') return monthRange(`${year}-${pad2(month)}`);
  if (kind === 'quarter') {
    const start = (quarter - 1) * 3 + 1;
    return { from: `${year}-${pad2(start)}-01`, to: `${year}-${pad2(start + 2)}-${pad2(lastDay(year, start + 2))}` };
  }
  if (kind === 'year') return { from: `${year}-01-01`, to: `${year}-12-31` };
  return range;
}

function titleOf(kind: Kind, year: number, month: number, quarter: number, from: string, to: string) {
  if (kind === 'month') return `${year}년 ${month}월 결산서`;
  if (kind === 'quarter') return `${year}년 ${quarter}분기 결산서`;
  if (kind === 'year') return `${year}년 연간 결산서`;
  return `${from} ~ ${to} 결산서`;
}

function SettlementDocument({ data, title }: { data: Settlement; title: string }) {
  const budgetOf = (type: string, category: string) => data.budgets.find((b) => b.type === type && b.category === category)?.total ?? 0;
  const showBudget = data.budgets.length > 0;
  const section = (type: '수입' | '지출') => {
    const rows = data.byCategory.filter((r) => r.type === type);
    const budgetOnly = data.budgets.filter((b) => b.type === type && !rows.some((r) => r.category === b.category));
    const all = [...rows, ...budgetOnly.map((b) => ({ type, category: b.category, total: 0, count: 0 }))];
    const total = type === '수입' ? data.income : data.expense;
    return (
      <table className="fin-doc-table">
        <thead>
          <tr>
            <th>{type === '수입' ? '수입 항목' : '계정과목'}</th>
            <th className="fin-num">금액</th>
            <th className="fin-num">구성비</th>
            {showBudget && <th className="fin-num">{data.budgetYear}년 {type === '수입' ? '목표' : '예산'}</th>}
            {showBudget && <th className="fin-num">{type === '수입' ? '달성률' : '집행률'}</th>}
          </tr>
        </thead>
        <tbody>
          {all.length === 0 && <tr><td colSpan={showBudget ? 5 : 3}>내역 없음</td></tr>}
          {all.map((r) => (
            <tr key={r.category}>
              <td>{r.category}</td>
              <td className="fin-num">{num(r.total)}</td>
              <td className="fin-num">{percent(r.total, total)}%</td>
              {showBudget && <td className="fin-num">{budgetOf(type, r.category) ? num(budgetOf(type, r.category)) : '-'}</td>}
              {showBudget && <td className="fin-num">{budgetOf(type, r.category) ? `${percent(r.total, budgetOf(type, r.category))}%` : '-'}</td>}
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <th>합계</th>
            <td className="fin-num"><b>{num(total)}</b></td>
            <td className="fin-num">100%</td>
            {showBudget && <td className="fin-num">{num(data.budgets.filter((b) => b.type === type).reduce((s, b) => s + b.total, 0))}</td>}
            {showBudget && <td className="fin-num">{percent(total, data.budgets.filter((b) => b.type === type).reduce((s, b) => s + b.total, 0))}%</td>}
          </tr>
        </tfoot>
      </table>
    );
  };

  const months = [...new Set(data.byMonth.map((m) => m.month))];

  return (
    <div className="fin-doc">
      <div className="fin-doc-head">
        <div>
          <h1>{title}</h1>
          <p>{data.church.churchName || '교회'} · 기간 {data.from} ~ {data.to} · 작성일 {todayValue()}</p>
        </div>
        <table className="fin-doc-sign">
          <tbody>
            <tr><th rowSpan={2}>결<br />재</th><th>재정부</th><th>재정부장</th><th>감사</th><th>담임목사</th></tr>
            <tr><td /><td /><td /><td /></tr>
          </tbody>
        </table>
      </div>

      <h2>1. 총괄</h2>
      <table className="fin-doc-table fin-doc-summary">
        <tbody>
          <tr><th>전기 이월</th><th>수입 (+)</th><th>지출 (−)</th><th>차기 이월</th></tr>
          <tr>
            <td className="fin-num">{num(data.carriedOver)}</td>
            <td className="fin-num">{num(data.income)}</td>
            <td className="fin-num">{num(data.expense)}</td>
            <td className="fin-num"><b>{num(data.closing)}</b></td>
          </tr>
        </tbody>
      </table>

      <h2>2. 수입 내역</h2>
      {section('수입')}

      <h2>3. 지출 내역 (계정과목별)</h2>
      {section('지출')}

      <h2>4. 부서별 지출</h2>
      <table className="fin-doc-table">
        <thead><tr><th>부서</th><th className="fin-num">금액</th><th className="fin-num">구성비</th></tr></thead>
        <tbody>
          {data.byDepartment.length === 0 && <tr><td colSpan={3}>내역 없음</td></tr>}
          {data.byDepartment.map((d) => (
            <tr key={d.department}><td>{departmentLabel(d.department === '공통' ? '' : d.department)}</td><td className="fin-num">{num(d.total)}</td><td className="fin-num">{percent(d.total, data.expense)}%</td></tr>
          ))}
        </tbody>
      </table>

      {months.length > 1 && (
        <>
          <h2>5. 월별 수지</h2>
          <table className="fin-doc-table">
            <thead><tr><th>월</th><th className="fin-num">수입</th><th className="fin-num">지출</th><th className="fin-num">차액</th></tr></thead>
            <tbody>
              {months.map((m) => {
                const inc = data.byMonth.find((r) => r.month === m && r.type === '수입')?.total ?? 0;
                const exp = data.byMonth.find((r) => r.month === m && r.type === '지출')?.total ?? 0;
                return <tr key={m}><td>{m}</td><td className="fin-num">{num(inc)}</td><td className="fin-num">{num(exp)}</td><td className="fin-num">{num(inc - exp)}</td></tr>;
              })}
            </tbody>
          </table>
        </>
      )}
      <p className="fin-doc-foot">위와 같이 결산하였음을 보고합니다.</p>
    </div>
  );
}

// 결산 보고서: 월간/분기/연간/기간 결산서(인쇄·PDF 저장·엑셀) + 주보 인쇄용 요약.
export default function SettlementReport({ ctx }: { ctx: FinanceCtx }) {
  const now = new Date();
  const [tab, setTab] = useState<'settlement' | 'bulletin'>('settlement');
  const [kind, setKind] = useState<Kind>('month');
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [quarter, setQuarter] = useState(Math.floor(now.getMonth() / 3) + 1);
  const [range, setRange] = useState(monthRange(todayValue().slice(0, 7)));
  const [data, setData] = useState<Settlement | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [printing, setPrinting] = useState(false);

  const period = useMemo(() => periodOf(kind, year, month, quarter, range), [kind, year, month, quarter, range]);
  const title = titleOf(kind, year, month, quarter, period.from, period.to);

  useEffect(() => {
    if (tab !== 'settlement' || !period.from || !period.to || period.from > period.to) return;
    setError(null);
    setData(null);
    financeGet<Settlement>(ctx, '/reports/settlement', period)
      .then(setData)
      .catch((err) => setError(errorMessage(err, '결산 자료를 불러오지 못했습니다.')));
  }, [ctx, period, tab]);

  const downloadExcel = () => {
    if (!data) return;
    const aoa: (string | number)[][] = [
      [title],
      [`기간 ${data.from} ~ ${data.to}`],
      [],
      ['전기 이월', '수입', '지출', '차기 이월'],
      [data.carriedOver, data.income, data.expense, data.closing],
      [],
      ['구분', '항목', '금액', '건수'],
      ...data.byCategory.map((r) => [r.type, r.category, r.total, r.count]),
      [],
      ['부서', '지출 금액'],
      ...data.byDepartment.map((d) => [d.department, d.total]),
    ];
    const ws = XLSX.utils.aoa_to_sheet(aoa);
    ws['!cols'] = [{ wch: 14 }, { wch: 16 }, { wch: 16 }, { wch: 14 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, '결산');
    XLSX.writeFile(wb, `${title.replace(/\s/g, '_')}.xlsx`);
  };

  return (
    <div className="manage-panel">
      <div className="manage-panel-header">
        <div>
          <h2 className="manage-panel-title">결산 보고서</h2>
          <p className="manage-panel-desc">월간·분기·연간 결산서를 자동으로 만듭니다. 인쇄 창에서 'PDF로 저장'을 고르면 PDF로 받을 수 있습니다.</p>
        </div>
      </div>

      <div className="manage-filterbar">
        <div className="manage-seg">
          <button type="button" className={`manage-seg-btn${tab === 'settlement' ? ' is-active' : ''}`} onClick={() => setTab('settlement')}>결산서</button>
          <button type="button" className={`manage-seg-btn${tab === 'bulletin' ? ' is-active' : ''}`} onClick={() => setTab('bulletin')}>주보용 요약</button>
        </div>
        {tab === 'settlement' && (
          <>
            <div className="manage-seg">
              {([['month', '월간'], ['quarter', '분기'], ['year', '연간'], ['range', '기간 지정']] as const).map(([k, label]) => (
                <button key={k} type="button" className={`manage-seg-btn${kind === k ? ' is-active' : ''}`} onClick={() => setKind(k)}>{label}</button>
              ))}
            </div>
            {kind !== 'range' && (
              <select className="fin-select" value={year} onChange={(e) => setYear(Number(e.target.value))}>
                {Array.from({ length: 6 }, (_, i) => now.getFullYear() - i).map((y) => <option key={y} value={y}>{y}년</option>)}
              </select>
            )}
            {kind === 'month' && (
              <select className="fin-select" value={month} onChange={(e) => setMonth(Number(e.target.value))}>
                {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => <option key={m} value={m}>{m}월</option>)}
              </select>
            )}
            {kind === 'quarter' && (
              <select className="fin-select" value={quarter} onChange={(e) => setQuarter(Number(e.target.value))}>
                {[1, 2, 3, 4].map((q) => <option key={q} value={q}>{q}분기</option>)}
              </select>
            )}
            {kind === 'range' && (
              <>
                <input type="date" value={range.from} onChange={(e) => setRange({ ...range, from: e.target.value })} />
                <span>~</span>
                <input type="date" value={range.to} onChange={(e) => setRange({ ...range, to: e.target.value })} />
              </>
            )}
            <span className="fin-spacer" />
            <button type="button" className="manage-btn manage-btn--sm" disabled={!data} onClick={downloadExcel}>엑셀</button>
            <button type="button" className="manage-btn manage-btn--sm manage-btn--primary" disabled={!data} onClick={() => setPrinting(true)}>🖨️ 인쇄 / PDF</button>
          </>
        )}
      </div>

      {tab === 'bulletin' && <BulletinSummary ctx={ctx} />}

      {tab === 'settlement' && (
        <>
          {error && <div className="manage-state manage-state--error">{error}</div>}
          {!error && !data && <div className="manage-state">불러오는 중입니다...</div>}
          {data && (
            <div className="fin-doc-preview">
              <SettlementDocument data={data} title={title} />
            </div>
          )}
          {data && !data.church.churchName && (
            <p className="fin-muted">교회 이름과 이월 잔액은 '재정 설정'에서 입력할 수 있습니다.</p>
          )}
        </>
      )}

      {printing && data && (
        <PrintPortal title={title} onDone={() => setPrinting(false)}>
          <SettlementDocument data={data} title={title} />
        </PrintPortal>
      )}
    </div>
  );
}
